// Service for importing Word documents into patdraft with strict validation
import JSZip from 'jszip';

// All allowed spec sections (all are optional)
const ALL_SPEC_SECTIONS = [
    'Field',
    'Background',
    'Brief Summary',
    'Brief Description of Drawings',
    'Detailed Description',
    'Exemplary Embodiments',
    'Examples',
    'Abstract'
];

// Claims section (the only required section)
const CLAIMS_SECTION = 'Claims';

class WordImportService {
    /**
     * Import a Word document and validate its structure
     * @param {File} file - The .docx file to import
     * @returns {Promise<Object>} - Parsed content with spec and claims
     */
    async importWordDocument(file) {
        if (!file.name.toLowerCase().endsWith('.docx')) {
            throw new Error('Only .docx files are supported for import');
        }

        try {
            const content = await this.parseWordDocument(file);
            this.validateDocumentStructure(content);
            
            const cleanedContent = this.cleanAndStructureContent(content);
            return cleanedContent;
        } catch (error) {
            console.error('Error during Word document import:', error);
            if (error.message.includes('Maximum call stack')) {
                throw new Error('Document contains complex formatting that cannot be processed. Please simplify the document formatting and try again.');
            }
            throw error; // Re-throw original error
        }
    }

    /**
     * Parse Word document and extract text with section headers
     * @param {File} file - The .docx file
     * @returns {Promise<Object>} - Raw extracted content
     */
    async parseWordDocument(file) {
        try {
            console.log('Parsing Word document:', {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            });
            
            const arrayBuffer = await file.arrayBuffer();
            console.log('ArrayBuffer size:', arrayBuffer.byteLength);
            
            // Debug: Log first few bytes of the file to check if it's a valid ZIP
            if (arrayBuffer.byteLength > 0) {
                const firstBytes = new Uint8Array(arrayBuffer.slice(0, Math.min(10, arrayBuffer.byteLength)));
                const hexBytes = Array.from(firstBytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
                console.log('First bytes (hex):', hexBytes);
                
                // ZIP files should start with "PK" (0x50 0x4B)
                if (firstBytes[0] === 0x50 && firstBytes[1] === 0x4B) {
                    console.log('File appears to be a valid ZIP file');
                } else {
                    console.warn('File does not appear to be a valid ZIP file');
                }
            }
            
            if (arrayBuffer.byteLength === 0) {
                throw new Error('Word document file is empty (0 bytes). Please check the export process.');
            }
            
            const zip = await JSZip.loadAsync(arrayBuffer);
            console.log('ZIP loaded successfully, files:', Object.keys(zip.files));
            
            // Extract document.xml (main content)
            const documentXmlFile = zip.file('word/document.xml');
            if (!documentXmlFile) {
                const availableFiles = Object.keys(zip.files);
                throw new Error(`Invalid Word document: missing document.xml. Available files: ${availableFiles.join(', ')}`);
            }
            
            const documentXml = await documentXmlFile.async('string');
            console.log('Document XML extracted, length:', documentXml.length);
            
            if (!documentXml || documentXml.length === 0) {
                throw new Error('Invalid Word document: document.xml is empty');
            }

            return this.extractTextWithSections(documentXml);
        } catch (error) {
            console.error('Error parsing Word document:', error);
            
            // Provide more specific error messages for common issues
            if (error.message.includes('End of data reached')) {
                throw new Error('Word document appears to be corrupted or empty. This may be due to an export issue. Please try exporting the document again.');
            } else if (error.message.includes('Corrupted zip')) {
                throw new Error('Word document file is corrupted. Please try exporting the document again.');
            } else if (error.message.includes('Not a valid zip file')) {
                throw new Error('File is not a valid Word document. Please ensure you are importing a .docx file.');
            }
            
            throw error; // Re-throw original error if it's not a known issue
        }
    }

    /**
     * Extract text content and identify sections from Word XML
     * @param {string} xml - Word document XML content
     * @returns {Object} - Structured content with sections and title
     */
    extractTextWithSections(xml) {
        // Parse XML to extract paragraphs and their styles
        const paragraphs = this.extractParagraphs(xml);
        const { sections, title } = this.identifySections(paragraphs);

        return {
            sections,
            title,
            rawParagraphs: paragraphs
        };
    }

    /**
     * Extract paragraphs from Word XML with text and style information
     * @param {string} xml - Word document XML
     * @returns {Array} - Array of paragraph objects
     */
    extractParagraphs(xml) {
        const paragraphs = [];
        
        // Match paragraph elements with safety limit
        const paragraphMatches = xml.match(/<w:p[^>]*>.*?<\/w:p>/gs) || [];
        
        // Safety check - limit to reasonable number of paragraphs
        const maxParagraphs = 10000;
        if (paragraphMatches.length > maxParagraphs) {
            throw new Error(`Document contains too many paragraphs (${paragraphMatches.length}). Maximum allowed: ${maxParagraphs}`);
        }
        
        for (const paragraphXml of paragraphMatches) {
            try {
                const text = this.extractTextFromParagraph(paragraphXml);
                const style = this.extractParagraphStyle(paragraphXml);
                const isHeading = this.isHeadingStyle(style, text);
                
                if (text.trim()) {
                    console.log(`Extracted paragraph: "${text.trim().substring(0, 50)}..." isHeading: ${isHeading}`, style);
                    paragraphs.push({
                        text: text.trim(),
                        style,
                        isHeading: isHeading,
                        xml: paragraphXml
                    });
                }
            } catch (error) {
                console.warn('Error processing paragraph, skipping:', error);
                // Continue processing other paragraphs
            }
        }
        
        return paragraphs;
    }

    /**
     * Extract plain text from a paragraph XML element
     * @param {string} paragraphXml - Single paragraph XML
     * @returns {string} - Plain text content
     */
    extractTextFromParagraph(paragraphXml) {
        // Safety check for paragraph size
        if (paragraphXml.length > 100000) {
            console.warn('Very large paragraph detected, truncating');
            paragraphXml = paragraphXml.substring(0, 100000);
        }
        
        // First, handle break elements by replacing them with newlines
        let processedXml = paragraphXml.replace(/<w:br[^>]*\/>/g, '\n');
        
        // Extract text from <w:t> elements with safety limit
        const textMatches = processedXml.match(/<w:t[^>]*>(.*?)<\/w:t>/gs) || [];
        
        // Safety check for too many text elements
        if (textMatches.length > 1000) {
            console.warn('Too many text elements in paragraph, limiting to first 1000');
            textMatches.splice(1000);
        }
        
        const texts = textMatches.map(match => {
            const textContent = match.replace(/<w:t[^>]*>(.*?)<\/w:t>/s, '$1');
            // Decode XML entities
            return textContent
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')
                .replace(/&quot;/g, '"')
                .replace(/&apos;/g, "'");
        });
        
        // Join extracted text
        let result = texts.join('');
        
        // Replace tab characters with spaces
        result = result.replace(/\t/g, ' ');
        
        // Clean up multiple spaces and newlines
        result = result.replace(/\s+/g, ' ');
        
        return result.trim();
    }

    /**
     * Extract paragraph style information
     * @param {string} paragraphXml - Paragraph XML
     * @returns {Object} - Style information
     */
    extractParagraphStyle(paragraphXml) {
        const style = {};
        
        // Look for style reference
        const styleMatch = paragraphXml.match(/<w:pStyle w:val="([^"]+)"/);
        if (styleMatch) {
            style.styleName = styleMatch[1];
        }
        
        // Check for bold
        if (paragraphXml.includes('<w:b/>') || paragraphXml.includes('<w:b w:val="true"')) {
            style.bold = true;
        }
        
        // Check for italic
        if (paragraphXml.includes('<w:i/>') || paragraphXml.includes('<w:i w:val="true"')) {
            style.italic = true;
        }
        
        // Check for underline
        if (paragraphXml.includes('<w:u ')) {
            style.underline = true;
        }
        
        return style;
    }

    /**
     * Determine if a paragraph style indicates a heading
     * @param {Object} style - Style object
     * @returns {boolean} - True if this appears to be a heading
     */
    isHeadingStyle(style, text = '') {
        // Check for explicit heading styles first
        if (style.styleName) {
            const headingPatterns = [
                /^heading/i,
                /^title/i,
                /^subtitle/i,
                /^header/i,
                /^h\d+/i
            ];
            
            if (headingPatterns.some(pattern => pattern.test(style.styleName))) {
                return true;
            }
        }
        
        // Special check: Don't treat numbered paragraphs as headings
        if (text.match(/^\[\d+\]/)) {
            return false;
        }
        
        // Check if text looks like a patent section heading
        const patentSectionPatterns = [
            /^(field|background|brief\s+summary|brief\s+description\s+of\s+drawings|detailed\s+description|exemplary\s+embodiments|examples|abstract|claims)$/i,
            /^(specification|summary|description|embodiments?)$/i
        ];
        
        if (patentSectionPatterns.some(pattern => pattern.test(text.trim()))) {
            return true;
        }
        
        // Fallback: if text is bold AND looks like a section heading (short, uppercase, etc.)
        if (style.bold === true) {
            // Only consider bold text as heading if it's short and looks like a title
            const trimmedText = text.trim();
            return trimmedText.length <= 50 && 
                   trimmedText === trimmedText.toUpperCase() &&
                   !trimmedText.match(/^\d+\./) && // Not a numbered item
                   !trimmedText.match(/^\[\d+\]/); // Not a numbered paragraph
        }
        
        return false;
    }

    /**
     * Identify sections based on headings and content
     * @param {Array} paragraphs - Array of paragraph objects
     * @returns {Object} - Sections mapped by name and title
     */
    identifySections(paragraphs) {
        console.log('Identifying sections from paragraphs:', paragraphs.length);

        const sections = {};
        let title = null;
        let currentSection = null;
        let currentContent = [];
        let isFirstHeading = true;

        for (const paragraph of paragraphs) {
            console.log('Processing paragraph:', {
                text: paragraph.text.substring(0, 50) + (paragraph.text.length > 50 ? '...' : ''),
                isHeading: paragraph.isHeading,
                style: paragraph.style
            });

            if (paragraph.isHeading) {
                // Check if this is the title (first heading with no content before next heading)
                if (isFirstHeading && currentContent.length === 0) {
                    // This could be the title - we'll confirm when we see the next heading
                    currentSection = paragraph.text;
                    currentContent = [];
                    isFirstHeading = false;
                    continue;
                }

                // If we had a section with no content, it might be the title (only set once)
                if (currentSection && currentContent.length === 0 && !title) {
                    title = currentSection;
                    console.log(`Identified title: "${title}"`);
                }

                // Save previous section (if it had content)
                if (currentSection && currentContent.length > 0) {
                    const sectionName = this.normalizeSectionName(currentSection);
                    sections[sectionName] = currentContent.join('\n\n');
                    console.log(`Saved section "${sectionName}" with ${currentContent.length} paragraphs`);
                }

                // Start new section
                const sectionName = this.normalizeSectionName(paragraph.text);
                console.log(`Starting new section: "${paragraph.text}" -> "${sectionName}"`);
                currentSection = paragraph.text;
                currentContent = [];
                isFirstHeading = false;
            } else if (currentSection !== null) {
                // Add content to current section
                currentContent.push(paragraph.text);
            }
        }

        // Save final section
        if (currentSection && currentContent.length > 0) {
            const sectionName = this.normalizeSectionName(currentSection);
            sections[sectionName] = currentContent.join('\n\n');
            console.log(`Saved final section "${sectionName}" with ${currentContent.length} paragraphs`);
        }

        console.log('Final sections found:', Object.keys(sections));
        if (title) {
            console.log('Document title found:', title);
        }

        return { sections, title };
    }

    /**
     * Normalize section names to match expected patent sections
     * @param {string} rawSectionName - Raw section name from document
     * @returns {string} - Normalized section name
     */
    normalizeSectionName(rawSectionName) {
        const normalized = rawSectionName.trim().toLowerCase();
        
        // Map common variations to standard names
        const sectionMappings = {
            'field': 'Field',
            'field of invention': 'Field',
            'field of the invention': 'Field',
            'technical field': 'Field',
            
            'background': 'Background',
            'background of invention': 'Background',
            'background of the invention': 'Background',
            'prior art': 'Background',
            
            'brief summary': 'Brief Summary',
            'summary': 'Brief Summary',
            'summary of invention': 'Brief Summary',
            'summary of the invention': 'Brief Summary',
            
            'brief description of drawings': 'Brief Description of Drawings',
            'brief description of the drawings': 'Brief Description of Drawings',
            'description of drawings': 'Brief Description of Drawings',
            'drawings': 'Brief Description of Drawings',
            
            'detailed description': 'Detailed Description',
            'detailed description of invention': 'Detailed Description',
            'detailed description of the invention': 'Detailed Description',
            'description': 'Detailed Description',
            
            'exemplary embodiments': 'Exemplary Embodiments',
            'embodiments': 'Exemplary Embodiments',
            'preferred embodiments': 'Exemplary Embodiments',
            
            'examples': 'Examples',
            'example': 'Examples',
            
            'abstract': 'Abstract',
            
            'claims': 'Claims',
            'claim': 'Claims'
        };
        
        return sectionMappings[normalized] || rawSectionName;
    }

    /**
     * Validate that the document contains required sections
     * @param {Object} content - Parsed document content
     * @throws {Error} - If validation fails
     */
    validateDocumentStructure(content) {
        const { sections } = content;
        const foundSections = Object.keys(sections);

        // Check for claims section (the only required section)
        if (!foundSections.includes(CLAIMS_SECTION)) {
            throw new Error(`Missing required Claims section. Found sections: ${foundSections.join(', ')}`);
        }

        // Check for unexpected sections (allow all spec sections + claims)
        const allowedSections = [...ALL_SPEC_SECTIONS, CLAIMS_SECTION];
        const unexpectedSections = foundSections.filter(
            section => !allowedSections.includes(section)
        );

        if (unexpectedSections.length > 0) {
            throw new Error(
                `Document contains unexpected sections: ${unexpectedSections.join(', ')}. ` +
                `Only the following sections are allowed: ${allowedSections.join(', ')}`
            );
        }

        // Note: All spec sections are optional - only Claims is required
    }

    /**
     * Clean and structure content for patdraft import
     * @param {Object} content - Raw parsed content
     * @returns {Object} - Cleaned content ready for import
     */
    cleanAndStructureContent(content) {
        const { sections, title } = content;

        // Clean text content - remove formatting artifacts and normalize
        // Handle Claims section differently to preserve line breaks
        const cleanedSections = {};
        for (const [sectionName, sectionContent] of Object.entries(sections)) {
            if (sectionName === CLAIMS_SECTION) {
                // Use special claims cleaning that preserves line breaks
                cleanedSections[sectionName] = this.cleanClaimsContent(sectionContent);
            } else {
                // Use regular cleaning for spec sections
                cleanedSections[sectionName] = this.cleanTextContent(sectionContent);
            }
        }

        // Structure for patdraft - use all spec sections (required + optional)
        const specContent = this.buildSpecificationContent(cleanedSections, ALL_SPEC_SECTIONS, title);
        const claimsContent = this.buildClaimsContent(cleanedSections[CLAIMS_SECTION]);

        return {
            spec: specContent,
            claims: claimsContent,
            metadata: {
                importedAt: new Date().toISOString(),
                originalSections: Object.keys(sections),
                title: title || null,
                fileType: 'docx'
            }
        };
    }

    /**
     * Clean text content - remove formatting artifacts and normalize
     * @param {string} text - Raw text content
     * @returns {string} - Cleaned text
     */
    cleanTextContent(text) {
        if (!text) return '';
        
        let cleaned = text;
        
        // Remove any remaining XML tags
        cleaned = cleaned.replace(/<[^>]+>/g, '');
        
        // Normalize whitespace
        cleaned = cleaned.replace(/\s+/g, ' ');
        
        // Remove leading/trailing whitespace
        cleaned = cleaned.trim();
        
        // Create paragraph breaks at numbered markers like [0001], [0002], etc.
        // Then remove the markers since paragraphs auto-number in the editor
        cleaned = cleaned.replace(/\s*\[\d+\]\s*/g, '\n\n');
        
        // Remove excessive line breaks
        cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n');
        
        return cleaned;
    }

    /**
     * Build specification content in patdraft format
     * @param {Object} sections - Cleaned sections
     * @param {Array} sectionOrder - Order of sections for spec
     * @param {string|null} title - Document title (optional)
     * @returns {string} - HTML formatted specification
     */
    buildSpecificationContent(sections, sectionOrder, title = null) {
        let specHtml = '';

        // Add title if present
        if (title) {
            specHtml += `<h1 data-type="title">${title}</h1>\n\n`;
        }

        for (const sectionName of sectionOrder) {
            const content = sections[sectionName];
            if (content) {
                specHtml += `<h2>${sectionName}</h2>\n`;

                // Split into paragraphs and wrap in <p> tags
                const paragraphs = content.split('\n\n').filter(p => p.trim());
                for (const paragraph of paragraphs) {
                    specHtml += `<p>${paragraph.trim()}</p>\n`;
                }

                specHtml += '\n';
            }
        }

        return specHtml.trim();
    }

    /**
     * Build claims content in patdraft format
     * @param {string} claimsText - Raw claims text
     * @returns {string} - Formatted claims text
     */
    buildClaimsContent(claimsText) {
        if (!claimsText) return '';
        
        // Just clean and return - preserve exact line structure from Word doc
        return this.cleanClaimsContent(claimsText);
    }

    /**
     * Clean claims content - preserve line breaks for proper claim formatting
     * @param {string} text - Raw claims text
     * @returns {string} - Cleaned claims text with preserved structure
     */
    cleanClaimsContent(text) {
        if (!text) return '';
        
        let cleaned = text;
        
        // Remove any remaining XML tags
        cleaned = cleaned.replace(/<[^>]+>/g, '');
        
        // Replace multiple spaces with single spaces, but preserve newlines
        cleaned = cleaned.replace(/[ \t]+/g, ' ');
        
        // Ensure we have proper single line breaks between claims/lines
        // Replace any instances where content is separated by spaces instead of newlines
        cleaned = cleaned.replace(/\s*\n\s*/g, '\n');
        
        // Remove leading/trailing whitespace from each line
        cleaned = cleaned.split('\n').map(line => line.trim()).filter(line => line).join('\n');
        
        // Remove leading/trailing whitespace
        cleaned = cleaned.trim();
        
        return cleaned;
    }

    /**
     * Format claims text to ensure proper numbering and structure
     * @param {string} claimsText - Raw claims text
     * @returns {string} - Properly formatted claims
     */
    formatClaimsText(claimsText) {
        // Split into potential claims
        const lines = claimsText.split('\n').map(line => line.trim()).filter(line => line);
        
        let formattedClaims = '';
        let claimNumber = 1;
        
        for (const line of lines) {
            // Check if line starts with a number (potential claim)
            const claimMatch = line.match(/^(\d+)\.\s*(.*)/);
            
            if (claimMatch) {
                // This looks like a claim
                if (formattedClaims) formattedClaims += '\n\n';
                formattedClaims += `${claimNumber}. ${claimMatch[2]}`;
                claimNumber++;
            } else if (formattedClaims) {
                // Continuation of previous claim
                formattedClaims += ` ${line}`;
            }
        }
        
        return formattedClaims;
    }
}

// Create and export singleton instance
export const wordImportService = new WordImportService();